import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Sparkles, 
  Target, 
  Ticket, 
  BarChart3, 
  Settings,
  LogOut,
  Crown,
  Menu,
  X,
  Shield,
  FileText,
  Mail,
  DollarSign,
  BookOpen,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";

const DashboardLayout = ({ children, currentPage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isAdmin = user?.email === "ai.manifest.official@gmail.com";
  const isPro = isAdmin || user?.subscription_tier === "pro" || user?.subscription_tier === "premium";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Build nav items based on role
  const navItems = [
    { icon: Sparkles, label: "Dashboard", path: "/dashboard", id: "dashboard" },
    { icon: Target, label: "Predictions", path: "/predictions", id: "predictions" },
    { icon: Ticket, label: "Games Tracking", path: "/tickets", id: "tickets" },
    { icon: BarChart3, label: "Analytics", path: "/analytics", id: "analytics" },
    { icon: FileText, label: "Reports", path: "/reports", id: "reports" },
    { icon: Crown, label: "Subscription", path: "/subscription", id: "subscription" },
    { icon: Users, label: "Community Stats", path: "/community-stats", id: "community-stats" },
  ];

  // Earn tab for ALL users (admin uses it as affiliate command center)
  navItems.push({ icon: DollarSign, label: "Earn", path: "/earn", id: "earn" });

  navItems.push(
    { icon: Settings, label: "Settings", path: "/settings", id: "settings" },
    { icon: Mail, label: "Contact Us", path: "/contact", id: "contact" }
  );

  // Admin-only nav items
  if (isAdmin) {
    navItems.push(
      { icon: Shield, label: "Admin", path: "/admin", id: "admin" },
      { icon: BookOpen, label: "Admin Books", path: "/admin-books", id: "admin-books" }
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-aura-surface border-r border-white/5 transform transition-transform lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/5 flex items-center justify-between">
            <div 
              className="logo cursor-pointer"
              onClick={() => navigate("/dashboard")}
            >
              <svg width="34" height="34" viewBox="0 0 100 100" className="glow">
                <defs>
                  <radialGradient id="ball-sidebar" cx="30%" cy="30%">
                    <stop offset="0%" stopColor="#444"/>
                    <stop offset="100%" stopColor="#000"/>
                  </radialGradient>
                </defs>
                <circle cx="50" cy="50" r="45" fill="url(#ball-sidebar)" />
                <circle cx="50" cy="50" r="18" fill="white"/>
                <text x="50" y="58" textAnchor="middle" fontSize="22" fontWeight="bold" fill="black">8</text>
              </svg>
              <span className="logo-text">AURA</span>
            </div>
            <button 
              className="lg:hidden text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  currentPage === item.id 
                    ? 'bg-aura-emerald/10 text-aura-emerald border border-aura-emerald/20' 
                    : 'text-aura-text-secondary hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {item.id === "subscription" && isPro && (
                  <span className="premium-badge ml-auto text-[10px]">PRO</span>
                )}
              </button>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-aura-emerald/20 flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-10 h-10 object-cover" />
                ) : (
                  <span className="text-aura-emerald font-medium">{user?.name?.[0]?.toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-aura-text-muted truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full border-white/10 text-aura-text-secondary hover:text-white hover:border-white/20"
              onClick={handleLogout}
              data-testid="sidebar-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="h-16 border-b border-white/5 flex items-center justify-between px-6 bg-aura-bg/80 backdrop-blur-xl sticky top-0 z-30">
          <button 
            className="lg:hidden text-white"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex-1 lg:flex-none" />

          <div className="flex items-center gap-3">
            {isPro ? (
              <span className="premium-badge">PRO</span>
            ) : (
              <Button 
                size="sm" 
                className="btn-gold text-xs"
                onClick={() => navigate("/subscription")}
                data-testid="header-upgrade-btn"
              >
                Upgrade
              </Button>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 relative">
          <div className="noise-overlay absolute inset-0 pointer-events-none" />
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
